import React from "react";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import dayjs, { Dayjs } from "dayjs";

import SVG from "@/assets/svg";
import CostumeButton from "@/component/button";
import { TCheckInCheckOut } from "@/component/drawer/DetailTab";
import { LabeledInput, TimeInput } from "@/component/input";
import { errorToast, successToast } from "@/component/toasts";
import { TASK } from "@/interfaces/IRoster";
import { Visit } from "@/interfaces/IServiceUser";
import { useAppSelector } from "@/redux/hooks";
import { updateLogById } from "@/services/roster";
import { ACCESS_LEVEL, LIMITS } from "@/utils/constants";
import logger from "@/runtime-utils/observability/logger";
import { DialogueStyled } from "../dialogueStyled";

interface Props {
  type: TASK;
  onClose: () => void;
  selectedLog:
    | (TCheckInCheckOut & {
        type: "checkIn" | "checkOut";
      })
    | null;
  fetchCheckInCheckOut: () => void;
}

function ManualCheckInOutInfo({
  selectedLog,
  type: actionType,
  onClose,
  fetchCheckInCheckOut,
}: Props) {
  const { selectedVisit } = useAppSelector((state) => state.roster);
  const visit = selectedVisit?.extendedProps as Visit;

  const taskType = actionType as TASK;
  const label = taskType === TASK.CHECK_IN ? "Check In" : "Check Out";

  const handleUpdate = async (data: {
    carer?: string;
    time: Date;
    note: string;
  }) => {
    try {
      //   logger.debug(data, `Updating manual ${label} info:`);
      const response = await updateLogById(selectedLog?._id, {
        ...(taskType === TASK.CHECK_IN
          ? { checkIn: data.time, checkInNote: data.note }
          : {}),
        ...(taskType === TASK.CHECK_OUT
          ? { checkOut: data.time, checkOutNote: data.note }
          : {}),
      });

      if (response.success) {
        successToast(`Successfully updated manual ${label} info.`);
        fetchCheckInCheckOut();
        onClose();
      }
      // After successful update, you might want to refresh data or give feedback to the user
    } catch (error: any) {
      logger.debug(`Error updating manual ${label} info:`, error);
      const err =
        error?.data?.response?.message ||
        error?.message ||
        "An unexpected error occurred.";
      errorToast(err);
      throw error;
    }
  };

  return (
    <DialogueStyled>
      <Box className="checkInConfirmation">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography id="modal-modal-title" variant="h3">
            Manual {label} Info For {selectedLog?.carer.name}
          </Typography>
          <Box>
            <SVG.Cross onClick={onClose} />
          </Box>
        </Box>
        <Box
          sx={{
            my: 2,
          }}
        >
          <Box
            sx={{
              fontSize: "12px",
              color: "gray",
              alignItems: "center",
              display: "flex",
              gap: "10px",
            }}
          >
            <SVG.Calender width="16" /> Planned Time:{" "}
            {dayjs(visit?.startTime).utc().format("dddd DD MMM YYYY • HH:mm")} -{" "}
            {dayjs(visit?.endTime).utc().format("HH:mm")}
          </Box>
        </Box>
        <Box>
          <Form
            taskType={taskType}
            visit={visit}
            carer={selectedLog?.carer._id}
            selectedLog={selectedLog || undefined}
            handleUpdate={handleUpdate}
          />
        </Box>
      </Box>
    </DialogueStyled>
  );
}

type PropsForm = {
  taskType: TASK;
  visit: Visit;
  carer?: string;
  handleUpdate: (data: {
    carer?: string;
    time: Date;
    note: string;
  }) => Promise<void>;
  selectedLog?: TCheckInCheckOut;
};

const Form = ({
  taskType,
  visit,
  carer,
  handleUpdate,
  selectedLog,
}: PropsForm) => {
  const [time, setTime] = React.useState<Dayjs | null>(
    selectedLog
      ? dayjs(selectedLog[taskType === TASK.CHECK_IN ? "checkIn" : "checkOut"])
      : dayjs(visit?.startTime).utc()
  );
  const [note, setNote] = React.useState(
    selectedLog?.[taskType === TASK.CHECK_IN ? "checkInNote" : "checkOutNote"]
  );
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const label = taskType === TASK.CHECK_IN ? "Check In" : "Check Out";
  const { user } = useAppSelector((state) => state.user);
  const isAdmin = [ACCESS_LEVEL.ADMINISTRATOR].includes(user.accessLevel);

  const validateNote = (value: string) => {
    if (!value || value.trim() === "") {
      return "Note is required";
    }
    if (value.length > LIMITS.description) {
      return `Note cannot exceed ${LIMITS.description} characters.`;
    }
    return "";
  };

  const handleNoteChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const inputValue = e.target.value;
    const validationError = validateNote(inputValue);
    setError(validationError);
    setNote(inputValue);
  };

  const handleSubmit = async () => {
    try {
      // Final validation before submit
      const validationError = validateNote(note);
      if (validationError) {
        setError(validationError);
        return;
      }

      setLoading(true);

      await handleUpdate({ carer, time: time.toDate(), note });
    } catch (error) {
      logger.debug(`Error updating manual ${label} info:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <TimeInput
        label={`${label} Time`}
        labelStyle={{
          padding: "12px 0px 0px 0px",
        }}
        minTime={dayjs(visit?.startTime).utc().subtract(2, "hour")}
        value={time}
        onChange={(value) => setTime(value)}
        disabled={!isAdmin || loading}
        // set max time to the check out time if taskType is CHECK_IN
        maxTime={
          taskType === TASK.CHECK_IN
            ? selectedLog?.checkOut
              ? dayjs(selectedLog?.checkOut).utc()
              : undefined
            : undefined
        }
      />
      {taskType === TASK.CHECK_IN && (
        <FormHelperText>
          Please note that the check in time must be less than the check out
          time
        </FormHelperText>
      )}

      <Typography
        sx={{
          fontWeight: "500",
          fontSize: "14px",
          color: "#1F4970",
        }}
      >
        Add note *
      </Typography>
      <LabeledInput
        value={note}
        placeholder="Add note"
        multiline
        rows={4}
        errorText={error}
        onChange={handleNoteChange}
        required
        disabled={!isAdmin || loading}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mt: 1,
        }}
      >
        <CostumeButton
          disabled={!isAdmin || !time || error.length > 0}
          loading={loading}
          className="Delete"
          sx={{
            width: "100% !important",
          }}
          onClick={handleSubmit}
        >
          Update
        </CostumeButton>
      </Box>
    </Box>
  );
};

export default ManualCheckInOutInfo;
